import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import {
  calculateGoldProduction,
  calculateUserDividends,
  CALCULATION_CONSTANTS,
  EXPANSION_PLAN,
  DIVIDEND_PROJECTIONS,
  DIVIDEND_DISCLAIMER,
  DYNAMIC_DENOMINATOR_NOTICE,
  SCALING_NOTICE,
  PLANT_ENGINEERING_LEAD_TIME_DAYS,
  computeDynamicAnnualProjection,
  PLANT_ON_SITE_NOTICE
} from '../lib/services/calculationService';
import { useLiveTotalSharesSold } from '../lib/hooks/useLiveTotalSharesSold';
import { DividendProjections2026to2035 } from '../components/dividends/DividendProjections2026to2035';
import { useGoldPrice } from '../hooks/useGoldPrice';
import { InfoTooltip, TERM_EXPLANATIONS } from '../components/common/InfoTooltip';
import { AnimatedNumber, AnimatedCurrency, AnimatedPercentage } from '../components/common/AnimatedNumber';
import { DividendJourneyTimeline } from '../components/calculator/DividendJourneyTimeline';
import { DividendPurchaseExamples } from '../components/calculator/DividendPurchaseExamples';
import { GoldPriceHistoricalChart } from '../components/calculator/GoldPriceHistoricalChart';
import { DividendReportExport } from '../components/calculator/DividendReportExport';
import { LiveActivityFeed } from '../components/calculator/LiveActivityFeed';
import { DIVIDENDS_USER_VISIBLE } from '../lib/dividendsUserVisible';
import '../styles/CalculatorPage.css';

interface CalculatorPageProps {
  onNavigate: (page: string) => void;
}

interface PhaseData {
  id: number;
  phase_number: number;
  phase_name: string;
  price_per_share: number;
  is_active: boolean;
}

const CalculatorPage: React.FC<CalculatorPageProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<string>('calculator');
  const [currentPhase, setCurrentPhase] = useState<PhaseData | null>(null);
  const [userShares, setUserShares] = useState<number>(100);
  const [_isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
  const howItWorksRef = useRef<HTMLDivElement>(null);

  // Get live gold price
  const { price: liveGoldPrice, pricePerOunce: liveGoldPricePerOunce, isLoading: _goldPriceLoading, isLive: isGoldPriceLive, source: goldPriceSource } = useGoldPrice();

  // Trigger animations when "How it Works" tab becomes visible
  useEffect(() => {
    if (activeTab === 'how-it-works') {
      // Small delay to ensure DOM is ready
      setTimeout(() => setIsHowItWorksVisible(true), 100);
    } else {
      setIsHowItWorksVisible(false);
    }
  }, [activeTab]);

  // Use live gold price with fallback to static constant
  const goldPricePerKg = liveGoldPrice || CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;
  const goldPricePerOz = liveGoldPricePerOunce || Math.round(goldPricePerKg / 31.1035);

  // Live total shares sold — dynamic denominator for Jul/Oct 2026 + Jan 2027
  const { totalSharesSold: liveSharesSold } = useLiveTotalSharesSold();

  // Fetch current active phase on mount
  useEffect(() => {
    const fetchActivePhase = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/public/phase-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operation: 'get_active_phase' })
        });
        const result = await response.json();

        if (response.ok && result.data?.phase) {
          setCurrentPhase(result.data.phase);
        } else {
          // Default to Phase 3 if no active phase found
          setCurrentPhase({
            id: 4,
            phase_number: 3,
            phase_name: 'Phase 3',
            price_per_share: 20,
            is_active: true
          });
        }
      } catch (err) {
        console.error('Error fetching active phase:', err);
        setCurrentPhase({
          id: 4,
          phase_number: 3,
          phase_name: 'Phase 3',
          price_per_share: 20,
          is_active: true
        });
      }
    };
    fetchActivePhase();
  }, []);

  // Profit uses the fixed projection gold price. Per share divides by shares sold so far.
  const projections = useMemo(() => {
    return Object.entries(EXPANSION_PLAN).map(([year, data]) => {
      const yearNum = parseInt(year);
      const projection = DIVIDEND_PROJECTIONS[yearNum as keyof typeof DIVIDEND_PROJECTIONS];
      const dyn = computeDynamicAnnualProjection(yearNum, liveSharesSold);
      const dividendPerShare = dyn?.estimatedDividendPerShare ?? 0;
      const userDividend = dividendPerShare * userShares;
      const goldKg = projection?.goldProductionKgPerYear || 0;
      const projectionPrice = CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;
      const netKeep = 1 - (CALCULATION_CONSTANTS.REFINING_PERCENT + CALCULATION_CONSTANTS.OPEX_PERCENT) / 100;

      return {
        year: yearNum,
        plants: data.plants,
        hectares: data.hectares,
        goldKg,
        revenue: goldKg * projectionPrice,
        ebit: goldKg * projectionPrice * netKeep,
        dividendPerShare,
        userDividend,
        isLiveDenominator: !!dyn,
        denominatorUsed: dyn?.denominatorUsed ?? 0,
        staticPerShareFloor: 0
      };
    });
  }, [userShares, liveSharesSold]);

  // Calculate 5-year totals
  const fiveYearTotal = useMemo(() => {
    return projections.reduce((sum, p) => sum + p.userDividend, 0);
  }, [projections]);

  // Calculate returns based on current phase price
  const fiveYearReturns = useMemo(() => {
    if (!currentPhase) return 0;
    const purchase = userShares * currentPhase.price_per_share;
    return purchase > 0 ? (fiveYearTotal / purchase) * 100 : 0;
  }, [userShares, currentPhase, fiveYearTotal]);

  // Legacy alias for compatibility
  const calculateROI = fiveYearReturns;

  // Calculate sharePrice, totalPurchase, roi, and yearlyDividends for components
  const sharePrice = currentPhase?.price_per_share || 15;
  const totalPurchase = userShares * sharePrice;
  const roi = fiveYearReturns;
  const yearlyDividends = projections.map(p => p.userDividend);

  useEffect(() => {
    if (!DIVIDENDS_USER_VISIBLE) onNavigate('home');
  }, [onNavigate]);

  const tabs = [
    { id: 'calculator', label: 'Dividend Calculator', icon: '🧮' },
    { id: 'how-it-works', label: 'How It Works', icon: '📚' }
  ];

  if (!DIVIDENDS_USER_VISIBLE) return null;

  return (
    <div className="page calculator-page-tabbed">
      {/* Tabbed Page Header */}
      <section className="tabbed-page-header">
        <div className="container">
          <div className="breadcrumb">
            <button onClick={() => onNavigate('home')} className="breadcrumb-link">
              Home
            </button>
            <span className="breadcrumb-separator">→</span>
            <span className="breadcrumb-current">Returns Calculator</span>
          </div>
          
          <div className="header-content">
            <div className="header-text">
              <h1 className="page-title">Dividend Calculator</h1>
              <p className="page-subtitle">
                Professional analysis tools for calculating your potential dividends on profits from gold mining operations
              </p>
              <p className="page-subtitle" style={{ color: '#D4AF37', fontWeight: 700 }}>
                {PLANT_ON_SITE_NOTICE}
              </p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-value">
                  <AnimatedCurrency value={goldPricePerKg / 1000} showDecimals={false} suffix="k" />
                </span>
                <span className="stat-label">Gold Price/kg {isGoldPriceLive ? '(Live)' : ''}</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  <AnimatedCurrency value={currentPhase?.price_per_share || 15} />
                </span>
                <span className="stat-label">Current Share Price</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  <AnimatedPercentage value={calculateROI} />
                </span>
                <span className="stat-label">5-Year Returns (<AnimatedNumber value={userShares} /> shares)</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="tab-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="tab-content-section">
        <div className="container">
          <div className="tab-content">
            
            {/* Calculator Tab */}
            {activeTab === 'calculator' && (
              <div className="tab-panel calculator-panel">
                <div className="panel-header">
                  <h2>Interactive Dividend Calculator</h2>
                  <p>Calculate your potential dividends on profits based on share ownership and production forecasts</p>
                </div>

                {/* Enhanced Share Calculator */}
                <div className="enhanced-share-calculator">
                  <div className="calculator-input-section">
                    <label className="calculator-label">Number of Shares</label>
                    <div className="calculator-input-wrapper">
                      <button
                        className="calculator-control-btn minus"
                        onClick={() => setUserShares(prev => Math.max(1, prev - 100))}
                      >
                        −
                      </button>
                      <input
                        id="shares-input"
                        type="number"
                        min="1"
                        max="100000"
                        value={userShares}
                        onChange={(e) => setUserShares(Math.max(1, parseInt(e.target.value) || 1))}
                        className="calculator-number-input"
                      />
                      <button
                        className="calculator-control-btn plus"
                        onClick={() => setUserShares(prev => prev + 100)}
                      >
                        +
                      </button>
                    </div>

                    {/* Slider */}
                    <div className="calculator-slider-wrapper">
                      <input
                        type="range"
                        min="1"
                        max="10000"
                        value={userShares}
                        onChange={(e) => setUserShares(parseInt(e.target.value))}
                        className="calculator-slider"
                      />
                      <div className="slider-labels">
                        <span>1</span>
                        <span>2,500</span>
                        <span>5,000</span>
                        <span>7,500</span>
                        <span>10,000</span>
                      </div>
                    </div>

                    {/* Shareholder Tier Presets */}
                    <div className="shareholder-tiers-section">
                      <span className="tiers-label">Choose Your Shareholder Level:</span>
                      <div className="shareholder-tiers-grid">
                        {[
                          { shares: 50, name: 'Starter', icon: '🌱', description: 'Begin your gold journey' },
                          { shares: 500, name: 'Growth', icon: '📈', description: 'Build your portfolio' },
                          { shares: 2500, name: 'Committed', icon: '💎', description: 'Serious wealth building' },
                          { shares: 10000, name: 'Founding', icon: '👑', description: 'Elite shareholder status' }
                        ].map(tier => {
                          const isActive = userShares === tier.shares;
                          const tierCost = currentPhase ? tier.shares * currentPhase.price_per_share : 0;
                          return (
                            <button
                              key={tier.shares}
                              onClick={() => setUserShares(tier.shares)}
                              className={`shareholder-tier-card ${isActive ? 'active' : ''}`}
                            >
                              <div className="tier-icon">{tier.icon}</div>
                              <div className="tier-name">{tier.name}</div>
                              <div className="tier-shares">{tier.shares.toLocaleString()} shares</div>
                              <div className="tier-cost">${tierCost.toLocaleString()}</div>
                              <div className="tier-description">{tier.description}</div>
                              {isActive && <div className="tier-selected-badge">✓ Selected</div>}
                            </button>
                          );
                        })}
                      </div>

                      {/* Quick amounts for custom selection */}
                      <div className="quick-amounts-row">
                        <span className="quick-amounts-label">Or quick select:</span>
                        <div className="quick-amounts-buttons">
                          {[10, 100, 250, 1000, 5000].map(amount => (
                            <button
                              key={amount}
                              onClick={() => setUserShares(amount)}
                              className={`quick-amount-btn ${userShares === amount ? 'active' : ''}`}
                            >
                              {amount.toLocaleString()}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Purchase Summary */}
                  <div className="investment-summary">
                    <div className="summary-item">
                      <span className="summary-label">Total Purchase:</span>
                      <span className="summary-value">
                        {currentPhase ? (
                          <AnimatedCurrency value={userShares * currentPhase.price_per_share} />
                        ) : '—'}
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Share Price:</span>
                      <span className="summary-value">
                        <AnimatedCurrency value={currentPhase?.price_per_share || 15} suffix="/share" />
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Current Phase:</span>
                      <span className="summary-value phase-badge">{currentPhase?.phase_name}</span>
                    </div>
                  </div>
                </div>

                {/* Dividend Projections Component - Same as Shareholder Dashboard */}
                <DividendProjections2026to2035
                  userShares={userShares}
                  variant="full"
                  showSharesInput={false}
                />

                {/* Dividend Journey Timeline */}
                <DividendJourneyTimeline
                  userShares={userShares}
                  goldPricePerKg={goldPricePerKg}
                />

                {/* What Could Your Dividends Buy */}
                <DividendPurchaseExamples
                  fiveYearDividends={fiveYearTotal}
                />

                {/* Gold Price Historical Chart */}
                <GoldPriceHistoricalChart
                  currentPrice={goldPricePerOz}
                  isLive={isGoldPriceLive}
                  source={goldPriceSource}
                />

                {/* Export & Share */}
                <DividendReportExport
                  shares={userShares}
                  sharePrice={sharePrice}
                  goldPricePerOz={goldPricePerOz}
                  yearlyDividends={yearlyDividends}
                  fiveYearTotal={fiveYearTotal}
                  totalPurchase={totalPurchase}
                  roi={roi}
                />
              </div>
            )}

            {/* How It Works Tab */}
            {activeTab === 'how-it-works' && (
              <div ref={howItWorksRef} className="tab-panel how-it-works-panel-upgraded">
                {/* Hero Section */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(0, 0, 0, 0.8) 100%)',
                  border: '2px solid #D4AF37',
                  borderRadius: '16px',
                  padding: '40px',
                  marginBottom: '32px',
                  textAlign: 'center'
                }}>
                  <h2 style={{ color: '#D4AF37', fontSize: '2rem', marginBottom: '12px', fontWeight: 'bold' }}>
                    💰 How Your Dividends Are Calculated
                  </h2>
                  <p style={{ color: '#9CA3AF', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
                    A transparent breakdown of our dividend calculation methodology from gold extraction to your wallet
                  </p>
                  <p style={{ color: '#6B7280', fontSize: '0.9rem', marginTop: '12px' }}>
                    <em>Hover over any <span className="info-icon" style={{ display: 'inline-flex', width: '16px', height: '16px', fontSize: '10px' }}>i</span> icon to learn more about a term</em>
                  </p>
                </div>

                {/* Process Flow Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: '24px',
                  marginBottom: '32px'
                }}>
                  {/* Step 1: Gold Production */}
                  <div className="calc-step-card" style={{
                    background: 'linear-gradient(180deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="step-badge" style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>1</div>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🏭</div>
                    <h3 style={{ color: '#D4AF37', marginBottom: '12px', fontSize: '1.2rem' }}>
                      Gold Production
                      <InfoTooltip title={TERM_EXPLANATIONS.MODULAR_PLANT.title}>
                        {TERM_EXPLANATIONS.MODULAR_PLANT.text}
                      </InfoTooltip>
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6' }}>
                      Each <strong style={{ color: '#D4AF37' }}>modular plant</strong> processes ore continuously to extract gold. Think of it like a gold factory that runs 24/7!
                    </p>
                    <div className="formula-display">
                      <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px' }}>
                        Per Plant Capacity:
                        <InfoTooltip title={TERM_EXPLANATIONS.THROUGHPUT.title}>
                          {TERM_EXPLANATIONS.THROUGHPUT.text}
                        </InfoTooltip>
                      </div>
                      <div style={{ color: '#D4AF37', fontSize: '1.1rem', fontFamily: 'monospace' }}>
                        <span className="formula-value">{CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH}</span>
                        <span className="formula-operator">t/hr ×</span>
                        <span className="formula-value">{CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY}</span>
                        <span className="formula-operator">hrs ×</span>
                        <span className="formula-value">{CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR}</span>
                        <span className="formula-operator">days</span>
                      </div>
                      <div className="formula-result" style={{ marginTop: '8px' }}>
                        = {(CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH * CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY * CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR).toLocaleString()} tonnes/year per plant
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.35)', color: '#bfdbfe', fontSize: '0.8rem', lineHeight: '1.5' }}>
                      <strong style={{ color: '#60a5fa' }}>1-plant baseline:</strong> the 2026 figures use a single (1) active 100 TPH plant at ~20.16 kg/month. Total annual yield &amp; resulting dividends scale linearly with the number of active plants on the land — roadmap targets <strong style={{ color: '#fff' }}>{EXPANSION_PLAN[2026].plants} plants by end 2026</strong> and <strong style={{ color: '#fff' }}>{EXPANSION_PLAN[2027].plants} plants by end 2027</strong>.
                    </div>
                  </div>

                  {/* Step 2: Gold Recovery */}
                  <div className="calc-step-card" style={{
                    background: 'linear-gradient(180deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="step-badge" style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>2</div>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>⚗️</div>
                    <h3 style={{ color: '#D4AF37', marginBottom: '12px', fontSize: '1.2rem' }}>Gold Recovery</h3>
                    <p style={{ color: '#9CA3AF', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6' }}>
                      Gold is extracted from processed ore using proven recovery methods. Not all gold can be captured - our <strong style={{ color: '#10B981' }}>95% recovery rate</strong> is industry-leading!
                    </p>
                    <div className="formula-display">
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                        <span style={{ color: '#6B7280' }}>
                          In-situ Grade:
                          <InfoTooltip title={TERM_EXPLANATIONS.GOLD_GRADE.title}>
                            {TERM_EXPLANATIONS.GOLD_GRADE.text}
                          </InfoTooltip>
                        </span>
                        <span className="formula-value">{CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE} g/tonne</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#6B7280' }}>
                          Recovery:
                          <InfoTooltip title={TERM_EXPLANATIONS.RECOVERY_RATE.title}>
                            {TERM_EXPLANATIONS.RECOVERY_RATE.text}
                          </InfoTooltip>
                        </span>
                        <span className="formula-result">baked into grade</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Revenue */}
                  <div className="calc-step-card" style={{
                    background: 'linear-gradient(180deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="step-badge" style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>3</div>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>💵</div>
                    <h3 style={{ color: '#D4AF37', marginBottom: '12px', fontSize: '1.2rem' }}>
                      Revenue Generation
                      <InfoTooltip title={TERM_EXPLANATIONS.GOLD_PRICE.title}>
                        {TERM_EXPLANATIONS.GOLD_PRICE.text}
                      </InfoTooltip>
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6' }}>
                      Dividend projections use a fixed gold price of <strong style={{ color: '#D4AF37' }}>${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg</strong>. The header quote is the live market price and is not the projection price.
                    </p>
                    <div className="formula-display">
                      <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Projection gold price:
                      </div>
                      <div className="gold-shimmer" style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                        ${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg
                      </div>
                      <div style={{ color: '#6B7280', fontSize: '0.85rem', marginTop: '8px' }}>
                        That's <strong style={{ color: '#D4AF37' }}>${goldPricePerOz.toLocaleString()}</strong> per troy ounce
                      </div>
                    </div>
                  </div>

                  {/* Step 4: EBIT */}
                  <div className="calc-step-card" style={{
                    background: 'linear-gradient(180deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="step-badge" style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>4</div>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📊</div>
                    <h3 style={{ color: '#D4AF37', marginBottom: '12px', fontSize: '1.2rem' }}>
                      Operating Profit (EBIT)
                      <InfoTooltip title={TERM_EXPLANATIONS.EBIT.title}>
                        {TERM_EXPLANATIONS.EBIT.text}
                      </InfoTooltip>
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6' }}>
                      After deducting operating costs (labor, fuel, maintenance), the remaining profit is called <strong style={{ color: '#D4AF37' }}>EBIT</strong> - this is the money available for dividends!
                    </p>
                    <div className="formula-display">
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
                        <span style={{ color: '#6B7280' }}>
                          Operating Costs:
                          <InfoTooltip title={TERM_EXPLANATIONS.OPEX.title}>
                            {TERM_EXPLANATIONS.OPEX.text}
                          </InfoTooltip>
                        </span>
                        <span style={{ color: '#EF4444', fontWeight: 'bold' }}>-{CALCULATION_CONSTANTS.OPEX_PERCENT}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid rgba(107, 114, 128, 0.3)' }}>
                        <span style={{ color: '#6B7280' }}>Your Profit Margin:</span>
                        <span className="formula-result">{100 - CALCULATION_CONSTANTS.OPEX_PERCENT}% of revenue</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 5: Dividend Per Share */}
                  <div className="calc-step-card" style={{
                    background: 'linear-gradient(180deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    border: '1px solid #374151',
                    borderRadius: '16px',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="step-badge" style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #D4AF37, #F4D03F)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>5</div>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎯</div>
                    <h3 style={{ color: '#D4AF37', marginBottom: '12px', fontSize: '1.2rem' }}>
                      Dividend Per Share
                      <InfoTooltip title={TERM_EXPLANATIONS.DIVIDEND.title}>
                        {TERM_EXPLANATIONS.DIVIDEND.text}
                      </InfoTooltip>
                    </h3>
                    <p style={{ color: '#9CA3AF', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6' }}>
                      The EBIT is divided equally among <strong style={{ color: '#D4AF37' }}>all shareholders</strong>. More shares = more dividends!
                    </p>
                    <div className="formula-display">
                      <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '8px' }}>
                        Total Shares Outstanding:
                        <InfoTooltip title={TERM_EXPLANATIONS.SHARES.title}>
                          {TERM_EXPLANATIONS.SHARES.text}
                        </InfoTooltip>
                      </div>
                      <div className="formula-value" style={{ fontSize: '1.3rem' }}>
                        {CALCULATION_CONSTANTS.TOTAL_SHARES.toLocaleString()} shares
                      </div>
                      <div style={{ color: '#6B7280', fontSize: '0.9rem', marginTop: '12px', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px' }}>
                        <span className="formula-value">Dividend/Share</span>
                        <span className="formula-operator">=</span>
                        <span>EBIT</span>
                        <span className="formula-operator">÷</span>
                        <span>{CALCULATION_CONSTANTS.TOTAL_SHARES.toLocaleString()}</span>
                      </div>
                    </div>
                    <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.35)', color: '#fbbf24', fontSize: '0.8rem', lineHeight: '1.5' }}>
                      📣 <strong>Shares sold:</strong> {DYNAMIC_DENOMINATOR_NOTICE}
                    </div>
                    <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.35)', color: '#bfdbfe', fontSize: '0.8rem', lineHeight: '1.55' }}>
                      <div style={{ color: '#60a5fa', fontWeight: 600, marginBottom: '4px' }}>🏗️ Scaling &amp; Expansion</div>
                      <p style={{ margin: '0 0 4px 0' }}><strong style={{ color: '#fff' }}>Baseline:</strong> {SCALING_NOTICE.baseline}</p>
                      <p style={{ margin: '0 0 4px 0' }}><strong style={{ color: '#fff' }}>Scaling potential:</strong> {SCALING_NOTICE.scalingPotential}</p>
                      <p style={{ margin: 0 }}><strong style={{ color: '#fff' }}>Operational lead time ({PLANT_ENGINEERING_LEAD_TIME_DAYS} days/plant):</strong> {SCALING_NOTICE.leadTime}</p>
                    </div>
                  </div>

                  {/* Step 6: Your Dividend */}
                  <div className="calc-step-card" style={{
                    background: 'linear-gradient(180deg, rgba(16, 185, 129, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
                    border: '2px solid #10B981',
                    borderRadius: '16px',
                    padding: '28px',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 0 30px rgba(16, 185, 129, 0.2)'
                  }}>
                    <div className="step-badge" style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #10B981, #34D399)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>✓</div>
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎉</div>
                    <h3 style={{ color: '#10B981', marginBottom: '12px', fontSize: '1.2rem' }}>Your Annual Dividend</h3>
                    <p style={{ color: '#9CA3AF', fontSize: '0.95rem', marginBottom: '16px', lineHeight: '1.6' }}>
                      The final step! Multiply your shares by the dividend per share to see <strong style={{ color: '#10B981' }}>your annual payout</strong>.
                    </p>
                    <div className="formula-display" style={{ borderLeftColor: '#10B981' }}>
                      <div style={{ color: '#6B7280', fontSize: '0.85rem', marginBottom: '12px' }}>Your Personalized Calculation:</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#10B981', fontSize: '1.4rem', fontWeight: 'bold' }}>{userShares.toLocaleString()}</span>
                        <span style={{ color: '#6B7280' }}>shares</span>
                        <span className="formula-operator">×</span>
                        <span style={{ color: '#D4AF37' }}>Dividend/Share</span>
                      </div>
                      <div className="formula-result" style={{ marginTop: '12px', fontSize: '1.4rem', padding: '12px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '8px', textAlign: 'center' }}>
                        = 💰 Your Annual Payout!
                      </div>
                    </div>
                  </div>
                </div>

                {/* Example Calculation - 2030 */}
                <div className="example-card" style={{
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(17, 24, 39, 0.95) 100%)',
                  border: '2px solid #D4AF37',
                  borderRadius: '16px',
                  padding: '32px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ color: '#D4AF37', marginBottom: '8px', fontSize: '1.4rem', textAlign: 'center' }}>
                    📈 Real Example: 2030 Projection
                  </h3>
                  <p style={{ color: '#9CA3AF', fontSize: '0.9rem', textAlign: 'center', marginBottom: '24px' }}>
                    With 200 modular plants operating, here's what your shareholding could look like:
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '16px'
                  }}>
                    <div className="projection-stat" style={{ textAlign: 'center', padding: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                      <div style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '4px' }}>
                        Material Processed
                        <InfoTooltip title={TERM_EXPLANATIONS.THROUGHPUT.title}>
                          {TERM_EXPLANATIONS.THROUGHPUT.text}
                        </InfoTooltip>
                      </div>
                      <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {(200 * CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH * CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY * CALCULATION_CONSTANTS.OPERATING_DAYS_PER_YEAR / 1000000).toFixed(1)}M tonnes
                      </div>
                    </div>
                    <div className="projection-stat" style={{ textAlign: 'center', padding: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                      <div style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '4px' }}>Gold Extracted</div>
                      <div className="gold-shimmer" style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {projections.find(p => p.year === 2030)?.goldKg.toFixed(0) || 'N/A'} kg
                      </div>
                    </div>
                    <div className="projection-stat" style={{ textAlign: 'center', padding: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                      <div style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '4px' }}>Total Revenue</div>
                      <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        ${((projections.find(p => p.year === 2030)?.revenue || 0) / 1000000).toFixed(1)}M
                      </div>
                    </div>
                    <div className="projection-stat" style={{ textAlign: 'center', padding: '16px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px' }}>
                      <div style={{ color: '#6B7280', fontSize: '0.8rem', marginBottom: '4px' }}>
                        Operating Profit
                        <InfoTooltip title={TERM_EXPLANATIONS.EBIT.title}>
                          {TERM_EXPLANATIONS.EBIT.text}
                        </InfoTooltip>
                      </div>
                      <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        ${((projections.find(p => p.year === 2030)?.ebit || 0) / 1000000).toFixed(1)}M
                      </div>
                    </div>
                    <div className="projection-stat" style={{ textAlign: 'center', padding: '20px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '12px', border: '2px solid #10B981' }}>
                      <div style={{ color: '#10B981', fontSize: '0.8rem', marginBottom: '4px' }}>Per Share Dividend</div>
                      <div style={{ color: '#10B981', fontSize: '1.6rem', fontWeight: 'bold' }}>
                        ${projections.find(p => p.year === 2030)?.dividendPerShare.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                    <div className="projection-stat" style={{ textAlign: 'center', padding: '20px', background: 'rgba(212, 175, 55, 0.2)', borderRadius: '12px', border: '2px solid #D4AF37' }}>
                      <div style={{ color: '#D4AF37', fontSize: '0.8rem', marginBottom: '4px' }}>🎉 YOUR {userShares} Shares Earn</div>
                      <div className="gold-shimmer" style={{ fontSize: '1.6rem', fontWeight: 'bold' }}>
                        ${projections.find(p => p.year === 2030)?.userDividend.toLocaleString(undefined, { maximumFractionDigits: 0 }) || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <p style={{ color: '#9CA3AF', fontSize: '0.9rem', margin: 0 }}>
                    <strong style={{ color: '#EF4444' }}>⚠️ Important:</strong> {DIVIDEND_DISCLAIMER}
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {/* Enhanced Disclaimer */}
      <section className="enhanced-disclaimer">
        <div className="container">
          <div className="disclaimer-header">
            <div className="disclaimer-icon">⚠️</div>
            <h3 className="disclaimer-title">Important Shareholder Disclaimer</h3>
          </div>

          <div className="disclaimer-content">
            <p>
              <strong>Shareholder Notice:</strong> All calculations and projections are based on current
              operational data, market conditions, and conservative estimates. Past dividend payments do not
              guarantee future dividend payments.
            </p>

            <div className="risk-factors">
              <h5>Key Risk Factors:</h5>
              <ul>
                <li>Gold prices are subject to market volatility and may fluctuate significantly</li>
                <li>Mining operations face inherent risks including weather, equipment, and regulatory changes</li>
                <li>Dividend payments depend on operational profitability and company performance</li>
                <li>Dividend payments are not guaranteed and may vary from projections</li>
              </ul>
            </div>

            <div className="disclaimer-footer">
              <p>
                <strong>Professional Advice:</strong> Please consult with qualified financial advisors
                before making share purchase decisions. This calculator is for informational purposes only.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Live Activity Feed */}
      <LiveActivityFeed />
    </div>
  );
};

export default CalculatorPage;
